import { useField, useFormikContext } from "formik";
import React from "react";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import ErrorField from "../errorField";
import { FieldResetWhenRemoved } from "../../util";
import classNames from "classnames";
import Icon from "../../../../assets/icons";
import Button from "../../../components/button";
import { getAssetUploadRecommendedCopy } from "../../core/assetUpload/util";
import { AssetUploadType } from "../../core/assetUpload/types";
import useDropZoneFileService from "../../dropZoneFileService";
import {
  ACCEPTED_FILE_MIME_TYPE,
  ACCEPTED_IMAGE_AND_PDF_MIME_TYPE,
  MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB
} from "../../../utils/constants";
import { useCopies } from "../../../hooks/useCopies";
import { AssetWithNameSelectFieldArgsType } from "./type";
import { getIconForAssetType } from "shared/utils";
import Variant from "shared/enums/Variant";

function AssetWithNameSelectField({
  fileSelectCallback,
  assetUploadType = AssetUploadType.FILE,
  ...props
}: AssetWithNameSelectFieldArgsType) {
  const copies = useCopies();
  const { setFieldTouched, setFieldValue } = useFormikContext();
  const [{ value }, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  const changeHandler = React.useCallback(
    async (f?: Array<File>) => {
      await setFieldValue(props.name, f?.[0] || "", Boolean(props.forceValidate));
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      fileSelectCallback?.(f?.[0]);
    },
    [setFieldValue, setFieldTouched, meta.touched, props.name, fileSelectCallback, props.forceValidate]
  );

  const dropzoneService = useDropZoneFileService({
    onFileSelect: changeHandler,
    disabled: props.disabled,
    maxSizeAllowed: props.assetConfig?.maxSize || MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB,
    acceptedMimeType:
      props.acceptedMimeType ||
      (assetUploadType === AssetUploadType.BOTH ? ACCEPTED_IMAGE_AND_PDF_MIME_TYPE : ACCEPTED_FILE_MIME_TYPE),
    assetConfig: props.assetConfig
  });

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
        <FormLabel
          label={props.label}
          required={props.required}
          additionalLabel={props.additionalLabel}
          infoLabel={props.infoLabel}
        />
        <div
          tabIndex={0}
          className={classNames("assetWithNameSelect", { "assetWithNameSelect--disabled": props.disabled })}>
          <div
            {...dropzoneService.dropzoneState?.getRootProps()}
            className={classNames("assetWithNameSelect--wrapper", {
              isDragActive: dropzoneService.dropzoneState?.isDragActive
            })}>
            <input {...dropzoneService.dropzoneState?.getInputProps()} />
            {value ? (
              <div className="assetWithNameSelect--file_selected">
                {getIconForAssetType(value.type)}
                {!props.disabled && (
                  <Button
                    className="closeIcon"
                    variant={Variant.ERROR}
                    icon={<Icon.CloseOutline />}
                    onClick={() => changeHandler()}
                  />
                )}
              </div>
            ) : (
              <div className="assetWithNameSelect--icon">
                {!props.disabled ? <Icon.CloudUpload /> : <Icon.NoImageFound />}
              </div>
            )}
            <div className="assetWithNameSelect--content text-center">
              {value ? (
                <React.Fragment>
                  {copies.get("update.asset.info.msg")}{" "}
                  <Button.Link
                    label={copies.get("browse")}
                    onClick={dropzoneService.dropzoneState?.open}
                    disabled={props.disabled}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {copies.get("upload.asset.info.msg")}{" "}
                  <Button.Link
                    label={copies.get("browse")}
                    onClick={dropzoneService.dropzoneState?.open}
                    disabled={props.disabled}
                  />
                  <div className="noteBlock">
                    {props.recommendedDisplayText ||
                      getAssetUploadRecommendedCopy({
                        copies,
                        assetUploadType,
                        config: props.assetConfig
                      })}
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
        <ErrorField meta={meta} />
        {props.enableFieldReset && <FieldResetWhenRemoved name={props.name} />}
      </FormGroup>
    </React.Fragment>
  );
}

export default AssetWithNameSelectField;
